import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect } from 'react';
import { Button } from 'reactstrap';

interface ModalConfig {
  title?: string;
  content: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  closeOnBackdropClick?: boolean;
  haveDefaultActionsButton?: boolean;
  closeOnConfirm?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: string;
  cancelVariant?: string;
}

interface ModalStackItem extends ModalConfig {
  id: string;
  level: number;
}

interface ModalContextType {
  showModal: (config: ModalConfig) => string | null;
  hideModal: (id?: string) => void;
  hideAllModals: () => void;
  isOpen: boolean;
  config: ModalConfig | null;
  modalStack: ModalStackItem[];
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);
const MAX_MODAL_STACK = 3;

export const ModalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [modalStack, setModalStack] = useState<ModalStackItem[]>([]);
  const [closingModals, setClosingModals] = useState<Set<string>>(new Set());
  const [newlyOpenedModals, setNewlyOpenedModals] = useState<Set<string>>(new Set());

  const showModal = useCallback((modalConfig: ModalConfig): string | null => {
    if (modalStack.length >= MAX_MODAL_STACK) {
      console.warn(`Maximum ${MAX_MODAL_STACK} modals allowed. Cannot open another modal.`);
      return null;
    }

    const modalId = `modal-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newModal: ModalStackItem = {
      ...modalConfig,
      id: modalId,
      level: modalStack.length,
    };

    setModalStack(prev => [...prev, newModal]);
    setNewlyOpenedModals(prev => new Set(prev).add(modalId));
    setTimeout(() => {
      setNewlyOpenedModals(prev => {
        const newSet = new Set(prev);
        newSet.delete(modalId);
        return newSet;
      });
    }, 300);

    return modalId;
  }, [modalStack.length]);

  const hideModal = useCallback((id?: string) => {
    setModalStack(prev => {
      if (prev.length === 0) return prev;

      const runAfterClose = (ids: string[], onCancels: (() => void)[]) => {
        setTimeout(() => {
          setModalStack(current => {
            if (id) {
              const i = current.findIndex(m => m.id === id);
              return i !== -1 ? current.slice(0, i) : current;
            }
            const lastId = ids[0];
            return current.length > 0 && current[current.length - 1].id === lastId ? current.slice(0, -1) : current;
          });
          setClosingModals(prevClosing => {
            const next = new Set(prevClosing);
            ids.forEach(modalId => next.delete(modalId));
            return next;
          });
          onCancels.forEach(cb => cb());
        }, 300);
      };

      const scheduleClosingState = (ids: string[]) => {
        setTimeout(() => {
          setClosingModals(prevClosing => {
            const next = new Set(prevClosing);
            ids.forEach(modalId => next.add(modalId));
            return next;
          });
          setNewlyOpenedModals(prev => {
            const next = new Set(prev);
            ids.forEach(modalId => next.delete(modalId));
            return next;
          });
        }, 0);
      };

      if (id) {
        const index = prev.findIndex(m => m.id === id);
        if (index === -1) return prev;
        const toClose = prev.slice(index);
        const ids = toClose.map(m => m.id);
        const onCancels = toClose.map(m => m.onCancel).filter((c): c is () => void => typeof c === 'function');
        scheduleClosingState(ids);
        runAfterClose(ids, onCancels);
        return prev;
      }

      const lastModal = prev[prev.length - 1];
      const ids = [lastModal.id];
      const onCancels = lastModal.onCancel ? [lastModal.onCancel] : [];
      scheduleClosingState(ids);
      runAfterClose(ids, onCancels);
      return prev;
    });
  }, []);

  const hideAllModals = useCallback(() => {
    setModalStack(prev => {
      if (prev.length === 0) return prev;
      const onCancels = prev.map(m => m.onCancel).filter((c): c is () => void => typeof c === 'function');
      const ids = prev.map(m => m.id);
      setTimeout(() => {
        setClosingModals(prevClosing => {
          const next = new Set(prevClosing);
          ids.forEach(modalId => next.add(modalId));
          return next;
        });
        setNewlyOpenedModals(new Set());
      }, 0);
      setTimeout(() => {
        setModalStack([]);
        setClosingModals(new Set());
        onCancels.forEach(cb => cb());
      }, 300);
      return prev;
    });
  }, []);

  const handleConfirm = useCallback((modalId: string) => {
    setModalStack(prev => {
      const modal = prev.find(m => m.id === modalId);
      if (!modal || modal.closeOnConfirm === false) return prev;
      const index = prev.findIndex(m => m.id === modalId);
      if (index === -1) return prev;
      const modalsToClose = prev.slice(index).map(m => m.id);
      const onConfirmCb = modal.onConfirm;
      setTimeout(() => {
        onConfirmCb?.();
        setClosingModals(prevClosing => {
          const next = new Set(prevClosing);
          modalsToClose.forEach(id => next.add(id));
          return next;
        });
        setNewlyOpenedModals(prev => {
          const next = new Set(prev);
          modalsToClose.forEach(id => next.delete(id));
          return next;
        });
      }, 0);
      setTimeout(() => {
        setModalStack(current => {
          const i = current.findIndex(m => m.id === modalId);
          return i !== -1 ? current.slice(0, i) : current;
        });
        setClosingModals(prevClosing => {
          const next = new Set(prevClosing);
          modalsToClose.forEach(id => next.delete(id));
          return next;
        });
      }, 300);
      return prev;
    });
  }, []);


  // Calculer le style pour chaque modal selon son niveau
  const getModalStyle = useCallback((level: number) => {
    // z-index progressif
    const zIndex = 1050 + level * 10;
    
    return {
      zIndex,
    };
  }, []);

  // Style pour le modal-dialog selon le niveau
  // Toutes les modals restent parfaitement centrées, avec seulement un léger scale pour l'effet visuel
  const getDialogStyle = useCallback((level: number, total: number) => {
    // Pas de décalage (translate) pour garder le centrage parfait
    // Léger scale seulement pour les modals en arrière-plan (effet visuel subtil)
    const scale = level === 0 ? 1 : Math.max(0.95, 1 - (level * 0.02));
    
    return {
      transform: `scale(${scale})`,
      transformOrigin: 'center center',
      opacity: 1,
    };
  }, []);

  // Initialiser les fonctions globales
  useEffect(() => {
    setGlobalModalFunctions(showModal, hideModal, hideAllModals);
  }, [showModal, hideModal, hideAllModals]);

  return (
    <ModalContext.Provider value={{ 
      showModal, 
      hideModal, 
      hideAllModals,
      isOpen: modalStack.length > 0, 
      config: modalStack.length > 0 ? modalStack[modalStack.length - 1] : null,
      modalStack 
    }}>
      {children}
      {modalStack.map((modal, index) => {
        const isTopModal = index === modalStack.length - 1;
        const modalStyle = getModalStyle(index);
        const dialogStyle = getDialogStyle(index, modalStack.length);
        const isClosing = closingModals.has(modal.id);
        const isNewlyOpened = newlyOpenedModals.has(modal.id);
        
        const handleClose = () => {
          hideModal(modal.id);
        };
        
        return (
          <React.Fragment key={modal.id}>
            {/* Backdrop seulement pour la modal du dessus */}
            {isTopModal && (
              <div
                className={`modal-backdrop fade show ${isClosing ? 'modal-backdrop-closing' : isNewlyOpened ? 'modal-backdrop-animated' : ''}`}
                style={{
                  zIndex: 1040 + index * 10,
                  opacity: 0.5,
                }}
                onClick={() => {
                  if (modal.closeOnBackdropClick !== false && !isClosing) {
                    handleClose();
                  }
                }}
              />
            )}
            <div
              className={`modal fade show ${isClosing ? 'modal-closing' : isNewlyOpened ? 'modal-animated' : ''}`}
              style={{
                display: 'block',
                zIndex: modalStyle.zIndex,
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: isTopModal && !isClosing ? 'auto' : 'none',
              }}
              onClick={(e) => {
                // Fermer seulement si on clique directement sur le backdrop (pas sur le contenu)
                if (e.target === e.currentTarget && isTopModal && modal.closeOnBackdropClick !== false && !isClosing) {
                  handleClose();
                }
              }}
            >
              <div
                className={`modal-dialog modal-dialog-centered modal-${modal.size || 'lg'} ${isClosing ? 'modal-dialog-closing' : isNewlyOpened ? 'modal-dialog-animated' : ''}`}
                style={{
                  ...dialogStyle,
                  position: 'relative',
                  margin: '1.75rem auto',
                  pointerEvents: 'auto',
                  maxWidth: modal.size === 'md' ? '500px' : undefined,
                  // Désactiver les transitions pour les modals déjà ouvertes (évite le flash)
                  transition: isNewlyOpened || isClosing ? undefined : 'none',
                }}
                onClick={(e) => {
                  // Empêcher la propagation du clic sur le contenu de la modal
                  e.stopPropagation();
                }}
              >
                <div className="modal-content" style={{ position: 'relative' }}>
                  {/* Bouton de fermeture en haut à droite (toujours affiché pour la modal du dessus) */}
                  {isTopModal && (
                    <button
                      type="button"
                      onClick={handleClose}
                      aria-label="Close"
                      style={{
                        position: 'absolute',
                        top: '16px',
                        right: '16px',
                        background: 'none',
                        border: 'none',
                        fontSize: '20px',
                        fontWeight: 300,
                        color: '#6b7280',
                        cursor: 'pointer',
                        opacity: 0.5,
                        zIndex: 10,
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'opacity 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '0.5'}
                    >
                      ×
                    </button>
                  )}
                  {modal.title && (
                    <div className="modal-header">
                      <h5 className="modal-title" style={{ margin: '16px' }}>
                        {modal.title}
                        {index > 0 && (
                          <span className="badge bg-secondary ms-2">Niveau {index + 1}</span>
                        )}
                      </h5>
                    </div>
                  )}
                  <div className="modal-body" style={ { padding: '16px 20px' } }>
                    <div style={{ padding: '16px 20px' }}>
                      {modal.content}
                    </div>
                  </div>
                  {modal.haveDefaultActionsButton !== false && (
                    <div className="modal-footer border-top" style={{ display: 'flex', justifyContent: 'flex-start', gap: '0.5rem', flexWrap: 'nowrap', padding: '16px 20px' }}>
                      {modal.onConfirm && (
                        <Button
                          color={modal.confirmVariant || 'primary'}
                          onClick={() => handleConfirm(modal.id)}
                          className="modal-confirm-btn"
                          style={{ 
                            borderRadius: '20px',
                            padding: '8px 24px',
                            fontSize: '14px',
                            fontWeight: 500,
                            border: 'none',
                            flex: 1,
                            minWidth: '150px'
                          }}
                        >
                          {modal.confirmLabel || 'Confirmer'}
                        </Button>
                      )}
                      <Button
                        color={modal.cancelVariant || 'secondary'}
                        onClick={handleClose}
                        className="modal-cancel-btn"
                        style={{ 
                          borderRadius: '20px',
                          padding: '8px 24px',
                          fontSize: '14px',
                          fontWeight: 500,
                          border: 'none',
                          flex: 1,
                          minWidth: '150px'
                        }}
                      >
                        {modal.cancelLabel || 'Annuler'}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </ModalContext.Provider>
  );
};

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};

// Fonctions globales pour faciliter l'utilisation
let globalShowModal: ((config: ModalConfig) => string | null) | null = null;
let globalHideModal: ((id?: string) => void) | null = null;
let globalHideAllModals: (() => void) | null = null;

export const setGlobalModalFunctions = (
  show: (config: ModalConfig) => string | null, 
  hide: (id?: string) => void,
  hideAll: () => void
) => {
  globalShowModal = show;
  globalHideModal = hide;
  globalHideAllModals = hideAll;
};

export const showModal = (config: ModalConfig): string | null => {
  if (globalShowModal) {
    return globalShowModal(config);
  } else {
    console.warn('ModalProvider not initialized. Make sure ModalProvider is mounted.');
    return null;
  }
};

export const hideModal = (id?: string) => {
  if (globalHideModal) {
    globalHideModal(id);
  }
};

export const hideAllModals = () => {
  if (globalHideAllModals) {
    globalHideAllModals();
  }
};

// Fonction helper pour afficher une modal de confirmation
export interface ConfirmModalConfig {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark';
  icon?: ReactNode;
  type?: 'delete' | 'warning' | 'info' | 'success' | 'danger';
  onConfirm: () => void;
  onCancel?: () => void;
}

export const showConfirmModal = (config: ConfirmModalConfig): string | null => {
  const {
    title = "Confirmation",
    message,
    confirmText = "Confirmer",
    cancelText = "Annuler",
    confirmVariant = "danger",
    icon,
    type = 'warning',
    onConfirm,
    onCancel,
  } = config;

  // Déterminer l'icône par défaut selon le type
  const getDefaultIcon = () => {
    if (icon) return icon;
    
    const iconStyle = {
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'transform 0.2s ease',
      flexShrink: 0,
    };
    
    switch (type) {
      case 'delete':
        return (
          <div style={{
            ...iconStyle,
            backgroundColor: '#ef4444',
            color: '#ffffff',
          }}>
            <i className="ri-delete-bin-line" style={{ fontSize: '14px' }}></i>
          </div>
        );
      case 'warning':
        return (
          <div style={{
            ...iconStyle,
            backgroundColor: '#f59e0b',
            color: '#ffffff',
          }}>
            <i className="ri-alert-line" style={{ fontSize: '14px' }}></i>
          </div>
        );
      case 'info':
        return (
          <div style={{
            ...iconStyle,
            backgroundColor: '#3b82f6',
            color: '#ffffff',
          }}>
            <i className="ri-information-line" style={{ fontSize: '14px' }}></i>
          </div>
        );
      case 'success':
        return (
          <div style={{
            ...iconStyle,
            backgroundColor: '#22c55e',
            color: '#ffffff',
          }}>
            <i className="ri-checkbox-circle-line" style={{ fontSize: '14px' }}></i>
          </div>
        );
      case 'danger':
      default:
        return (
          <div style={{
            ...iconStyle,
            backgroundColor: '#ef4444',
            color: '#ffffff',
          }}>
            <i className="ri-error-warning-line" style={{ fontSize: '14px' }}></i>
          </div>
        );
    }
  };

  return showModal({
    title: undefined,
    size: "md",
    content: (
      <div style={{ padding: '20px' }} className="confirm-modal-content">
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
          {/* Icône à gauche */}
          <div className="confirm-modal-icon" style={{
            animation: 'fadeInScale 0.3s ease-out',
            flexShrink: 0,
          }}>
            {getDefaultIcon()}
          </div>
          
          {/* Contenu texte à droite */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Titre */}
            <h5 style={{ 
              fontWeight: 600,
              fontSize: '16px',
              margin: '0 0 8px 0',
              color: 'var(--vz-heading-color, #111827)',
              lineHeight: '1.4'
            }}>
              {title}
            </h5>
            
            {/* Message */}
            <p style={{
              fontSize: '14px',
              lineHeight: '1.5',
              margin: '0 0 16px 0',
              color: 'var(--vz-body-color, #6b7280)',
            }}>
              {message}
            </p>
          </div>
        </div>
      </div>
    ),
    haveDefaultActionsButton: true,
    closeOnBackdropClick: false,
    confirmLabel: confirmText,
    cancelLabel: cancelText,
    confirmVariant,
    onConfirm: () => {
      onConfirm();
    },
    onCancel: () => {
      if (onCancel) {
        onCancel();
      }
    },
  });
};
